// ─────────────────────────────────────────────────────────────────────────────
// PRODUCT design tokens — the Attio-inspired visual language of the internal OS
// app, documented at /styleguide. Lifted verbatim from the live surfaces
// (company-list.tsx) so the styleguide, the accounts grid, and the dashboard
// cards share one source and can't drift.
// See `src/app/(authenticated)/styleguide/page.tsx`.
//
// NOT the client-facing brand. Anything a CLIENT sees — proposals, decks, PDFs,
// the portal, marketing pages — uses the Setsail marketing brand in
// `./brand.ts`, backed by the full design system vendored at `design-system/`
// (143 components, 73 guideline cards, 14 page templates, 14 slide layouts).
// Agents reach that over MCP: `design_system`, `design_tokens`,
// `design_component`, `design_guideline`, `design_template`.
// ─────────────────────────────────────────────────────────────────────────────

import { cn } from "@/lib/utils"

/** Compact diagnostic rows in More → System Status. */
export const SYSTEM_STATUS_ROW = "flex items-center justify-between gap-2 px-1.5 py-1 text-xs"
export const SYSTEM_STATUS_COLOR = {
  ok: { dot: "bg-green-500", detail: "text-green-600 dark:text-green-400" },
  warning: { dot: "bg-amber-500", detail: "text-amber-600 dark:text-amber-400" },
  error: { dot: "bg-red-500", detail: "text-red-600 dark:text-red-400" },
} as const

// Attio chip palette — pale pastel fill, saturated text, subtle matching border,
// with dark fallbacks. Keyed off CompanyStatus.
export const STATUS_COLOR: Record<string, string> = {
  prospect:
    "border border-[#d6e5ff] bg-[#e5eeff] text-[#215bc4] dark:border-sky-900/40 dark:bg-sky-950/40 dark:text-sky-300",
  active:
    "border border-[#cbf7e1] bg-[#e0fced] text-[#007d53] dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-300",
  passive:
    "border border-[#fbe6bf] bg-[#fef4e0] text-[#b06f12] dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-300",
  inactive:
    "border border-[#e6e7ea] bg-[#f3f4f6] text-[#5f5e5b] dark:border-zinc-700/50 dark:bg-zinc-800/60 dark:text-zinc-300",
  churned:
    "border border-[#ffd1d1] bg-[#ffebeb] text-[#ba2525] dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-300",
}

/**
 * Where a Profile value came from (Profile rows, Jason 2026-11-01): typed by a
 * person, filled by AI research, read from Google, or empty. Same Attio
 * pastel family as STATUS_COLOR; Google takes the lavender.
 */
export const SOURCE_COLOR: Record<"typed" | "ai" | "google" | "empty", string> = {
  typed: STATUS_COLOR.active,
  ai: STATUS_COLOR.prospect,
  google:
    "border border-[#e2d6ff] bg-[#efe8ff] text-[#6b3fc4] dark:border-violet-900/40 dark:bg-violet-950/40 dark:text-violet-300",
  empty: STATUS_COLOR.inactive,
}

export const SOURCE_LABEL: Record<"typed" | "ai" | "google" | "empty", string> = {
  typed: "Typed",
  ai: "AI",
  google: "Google",
  empty: "Empty",
}

export const STATUS_DOT: Record<string, string> = {
  prospect: "bg-sky-500",
  active: "bg-emerald-500",
  passive: "bg-amber-500",
  inactive: "bg-zinc-400",
  churned: "bg-rose-500",
}

// Saturated health-zone dot — pairs with HEALTH_COLOR, same family as
// STATUS_DOT. Used for the small indicator dot on stat/KPI-card titles.
export const HEALTH_DOT: Record<string, string> = {
  green: "bg-emerald-500",
  amber: "bg-amber-500",
  red: "bg-rose-500",
}

// Account-health zone pill — same Attio pastel family as the status chips,
// keyed off a health zone ("red" | "amber" | "green").
export const HEALTH_COLOR: Record<string, string> = {
  green:
    "border border-[#cbf7e1] bg-[#e0fced] text-[#007d53] dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-300",
  amber:
    "border border-[#ffe6c0] bg-[#fff4e0] text-[#b25f00] dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-300",
  red:
    "border border-[#ffd1d1] bg-[#ffebeb] text-[#ba2525] dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-300",
}

// Booked-out day box — the Launchpad 14-day strip (Jason 2026-08-18). Keyed
// off a BookedBand from src/lib/team/booked-out.ts. Two reds on purpose:
// "red" is empty and "over" is over-booked — both wrong, fixed in opposite
// directions, so they must not look the same. "off" is a weekend/holiday.
export const BOOKED_BAND_COLOR: Record<string, string> = {
  green:
    "border border-[#cbf7e1] bg-[#e0fced] text-[#007d53] dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-300",
  yellow:
    "border border-[#fbe6bf] bg-[#fef4e0] text-[#b06f12] dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-300",
  orange:
    "border border-[#ffddc0] bg-[#fff0e0] text-[#c05621] dark:border-orange-900/40 dark:bg-orange-950/40 dark:text-orange-300",
  red:
    "border border-[#ffd1d1] bg-[#ffebeb] text-[#ba2525] dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-300",
  over:
    "border border-[#b91c1c] bg-[#7f1d1d] text-white dark:border-red-800 dark:bg-red-950 dark:text-red-200",
  off:
    "border border-[#e6e7ea] bg-[#f3f4f6] text-[#9b9a97] dark:border-zinc-700/50 dark:bg-zinc-800/60 dark:text-zinc-500",
}

// Sales Start side stripes (Jason 2026-08-18): green band = the sales side
// (pipeline), purple band = the delivery side (billing). A band, not a
// full-page background — full-page colour fights the V2 surface.
export const SIDE_STRIPE_COLOR: Record<string, string> = {
  sales:
    "border border-[#cbf7e1] bg-[#e0fced] text-[#007d53] dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-300",
  delivery:
    "border border-[#e2d6ff] bg-[#efe8ff] text-[#6b3fc4] dark:border-violet-900/40 dark:bg-violet-950/40 dark:text-violet-300",
}

// Task/work-item priority chip — same Attio pastel family as the status
// chips. Pair with CHIP. Keyed off a priority string ("critical" | "high" |
// "medium" | "low"); unrecognized values should fall back to "low".
export const PRIORITY_COLOR: Record<string, string> = {
  critical:
    "border border-[#ffd1d1] bg-[#ffebeb] text-[#ba2525] dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-300",
  high:
    "border border-[#ffe0c2] bg-[#fff1e0] text-[#b0530f] dark:border-orange-900/40 dark:bg-orange-950/40 dark:text-orange-300",
  medium:
    "border border-[#ffe6c0] bg-[#fff4e0] text-[#b25f00] dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-300",
  low:
    "border border-[#e6e7ea] bg-[#f3f4f6] text-[#5f5e5b] dark:border-zinc-700/50 dark:bg-zinc-800/60 dark:text-zinc-300",
}

// Catalog service-kind chip — `rfp` marks a service that exists only to price
// RFP/enterprise bids and is excluded from every proactive-quoting surface.
// Violet deliberately sits outside the status/health/priority families: those
// describe what state a record is IN, this describes what a service IS. Pair
// with CHIP.
export const SERVICE_KIND_COLOR: Record<string, string> = {
  rfp:
    "border border-[#e2d9fb] bg-[#f1ecfe] text-[#6035c9] dark:border-violet-900/40 dark:bg-violet-950/40 dark:text-violet-300",
}

// Deterministic avatar palette — same Attio pastel family used for the
// rounded-square company glyph and the round contact glyph.
export const AVATAR_COLORS = [
  "bg-orange-100 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300",
  "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
  "bg-teal-100 text-teal-700 dark:bg-teal-950/50 dark:text-teal-300",
  "bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300",
  "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300",
  "bg-pink-100 text-pink-700 dark:bg-pink-950/50 dark:text-pink-300",
  "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300",
]

export function colorForName(name: string): string {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[h % AVATAR_COLORS.length]
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Resolve a company's website to a favicon URL (Google's favicon service).
 *  Returns null when there's no usable domain — the glyph falls back to
 *  initials. Canonical source for the company-glyph favicon pattern used by
 *  the accounts grid, calls list, account hover card, and revenue grid. */
export function faviconUrl(website: string | null | undefined): string | null {
  if (!website) return null
  try {
    const host = new URL(
      website.includes("://") ? website : `https://${website}`,
    ).hostname
    if (!host) return null
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=64`
  } catch {
    return null
  }
}

/**
 * Canonical height for every interactive control that sits in a page subheader /
 * toolbar — search fields, filter toggles, date pickers, segmented switchers, and
 * toolbar buttons. Matches the global sidebar-search box (h-9 / 36px) so the whole
 * row lines up. Use this on subheader Inputs/Buttons; SEGMENT_TRACK already bakes
 * it in. Standard for all pages.
 *
 * On coarse pointers (touch devices) the control grows to h-10 (40px) toward the
 * 44px touch-target guideline without disturbing desktop layouts — see the
 * "Mobile & touch" styleguide section.
 */
export const SUBHEADER_CONTROL_H = "h-9 pointer-coarse:h-10"

// ── Buttons ─────────────────────────────────────────────────────────────────

/**
 * Shared sizing for every flat button — one height (h-8 / 32px), 13px medium
 * label, pill-ish rounded-lg. Compose with one of the fill tokens below.
 */
/**
 * `cursor-pointer` is here on purpose and must not be dropped.
 *
 * Tailwind v3's Preflight set `button { cursor: pointer }`. **v4 removed it** —
 * its reset now only sets `appearance: button`, deferring the cursor to the
 * browser default, which for `<button>` is an arrow. Nothing in this codebase
 * put it back: there is no `cursor` rule in any CSS file and none in this
 * module.
 *
 * The result was that every button styled with BUTTON_BASE — 107 of them
 * across 26 files — looked inert on hover. Jason reported it as "rescan folder
 * button is not clickable in documents tab" (2026-07-27). The button worked;
 * it just never said so, which is the same thing to whoever is using it.
 *
 * Fixed at the token rather than the one button, because the one button was
 * never the bug.
 */
export const BUTTON_BASE =
  "inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg px-2.5 text-[13px] font-medium transition-colors pointer-coarse:h-10 disabled:cursor-not-allowed disabled:opacity-60"

/**
 * The three "major clickable" primary fills. Each carries a soft drop shadow so
 * it lifts off the surface — reserve these for the dominant action in a region.
 *
 *  • PRIMARY_BUTTON — emerald, the everyday default primary action.
 *  • WHITE_BUTTON  — white fill + black text, the quieter sibling.
 *  • BLACK_BUTTON  — black fill + white text, RARELY used; rare standout moments.
 *
 * Pair with BUTTON_BASE: cn(BUTTON_BASE, PRIMARY_BUTTON).
 */
/** Leaf (#158E1D) — the brand primary, matching `--primary` in globals.css so a
 *  plain <Button> and a PRIMARY_BUTTON are the same colour. They were not:
 *  --primary was near-black and this was emerald-600. */
export const PRIMARY_BUTTON =
  "bg-[#158E1D] text-white shadow-[0_1px_2px_0_rgba(28,40,64,0.18),0_2px_6px_-1px_rgba(28,40,64,0.16)] hover:bg-[#127919]"

export const WHITE_BUTTON = cn(
  "border border-[#e5e7ea] bg-white text-[#242529] hover:bg-[#F8F9FA]",
  "shadow-[0_1px_2px_0_rgba(28,40,64,0.13),0_2px_6px_-1px_rgba(28,40,64,0.10)]",
  "dark:border-input dark:bg-input/30 dark:text-foreground dark:shadow-none dark:hover:bg-input/50",
)

export const BLACK_BUTTON = cn(
  "bg-[#18181b] text-white hover:bg-[#2a2a2e]",
  "shadow-[0_1px_2px_0_rgba(28,40,64,0.22),0_2px_6px_-1px_rgba(28,40,64,0.20)]",
  "dark:bg-white dark:text-[#18181b] dark:hover:bg-zinc-200",
)

/** The toolbar / button surface AGENTS.md names: WHITE_BUTTON by its other
 *  name — hairline border, soft shadow, hover #F8F9FA. One token, two names,
 *  so the styleguide's rule and the code agree. */
export const BUTTON_SURFACE = WHITE_BUTTON

/** Flat white button — same hairline-bordered surface as WHITE_BUTTON but WITHOUT
 *  the soft drop shadow. Use inside dense surfaces (table cells, list rows) where
 *  the raised 3D look reads as visual noise. */
export const WHITE_BUTTON_FLAT = cn(
  "border border-[#e5e7ea] bg-white text-[#242529] shadow-none hover:bg-[#F8F9FA]",
  "dark:border-input dark:bg-input/30 dark:text-foreground dark:hover:bg-input/50",
)

/** Quiet app-bar search: flat surface, with a thin focus border rather than a halo. */
export const GLOBAL_SEARCH_FIELD = cn(WHITE_BUTTON_FLAT, SUBHEADER_CONTROL_H,
  "w-full rounded-lg has-[[data-slot=input-group-control]:focus-visible]:ring-0 has-[[data-slot=input-group-control]:focus-visible]:border-emerald-600")
export const GLOBAL_SEARCH_RESULTS = "w-[min(28rem,calc(100vw-2rem))] gap-0 overflow-hidden border border-border p-0 shadow-none ring-0"

// ── Reusable class fragments ────────────────────────────────────────────────

/** Data grid — the accounts-table style (styleguide "Data grid"): border-separate,
 *  hairline column + row dividers, 36px rows, 13px–14px text. Use with the
 *  Table primitives: `<Table className={DATA_GRID_TABLE}>`, header row
 *  `DATA_GRID_HEAD_ROW` + `DATA_GRID_TH` cells, body rows `DATA_GRID_ROW` +
 *  `DATA_GRID_TD` cells. Add `table-fixed` + a <colgroup> when it shares width. */
export const DATA_GRID_DIVIDER = "border-border/60"
export const DATA_GRID_TABLE = "border-separate border-spacing-0"
export const DATA_GRID_HEAD_ROW = "border-b-0 hover:bg-transparent [&>th]:border-b [&>th:last-child]:border-r-0"
export const DATA_GRID_ROW = "group border-b-0 hover:bg-muted/40 [&>td]:border-b [&>td:last-child]:border-r-0"
export const DATA_GRID_TH = `h-9 px-3 text-sm font-medium text-[#242529] dark:text-foreground border-r ${DATA_GRID_DIVIDER}`
export const DATA_GRID_TD = `h-9 px-3 py-0 text-sm border-r ${DATA_GRID_DIVIDER}`

/** Pastel status/health chip base. Pair with STATUS_COLOR[..] / HEALTH_COLOR[..]. */
export const CHIP =
  "inline-flex items-center rounded-[5px] px-1.5 py-0.5 text-[14px] leading-5 font-medium"

/** Numeric health pill — same chip, semibold tabular figures. */
export const HEALTH_PILL =
  "inline-flex items-center rounded-[5px] px-1.5 py-0.5 text-sm font-semibold tabular-nums"

/**
 * A score that no longer describes what it graded — compose AFTER a
 * HEALTH_COLOR so the band stays readable but the pill steps back. Dashed edge
 * + faded fill; always pair with a tooltip saying why (e.g. the Bid Engine's
 * cold read after the files changed: "files changed since this read").
 */
export const HEALTH_PILL_STALE = "border-dashed opacity-55"

// ── Top-bar count pill ──────────────────────────────────────────────────────
// One control: ICON + NUMBER, side by side (Jason 2026-08-27: "I can't see the
// icon behind the notifications. We need to decouple the two into 1 pill.
// Pill = (Icon + Number)").
//
// It replaced an absolutely-positioned badge pinned to the icon's top-right
// corner. That works at "3" and fails at "9+": the badge is wider than the
// corner it sits in, so it covers the glyph and the control loses the only
// thing that said what it was. Two of them side by side — mail and bell — read
// as two red blobs.
//
// Laying the count BESIDE the glyph means the pill grows sideways instead of
// over itself, so "9+" and "127" are equally legible and the icon is never
// occluded. Colours are the house rose pastel (STATUS_COLOR.churned), not a
// saturated red-500 dot.

/** Shared frame. Compose with COUNT_PILL_IDLE or COUNT_PILL_ACTIVE. */
export const COUNT_PILL =
  "inline-flex h-8 shrink-0 cursor-pointer items-center gap-1 rounded-full transition-colors focus:outline-none"

/** Nothing waiting — a plain square icon button, no pill surface. */
export const COUNT_PILL_IDLE =
  "w-8 justify-center px-0 text-muted-foreground hover:bg-muted hover:text-foreground"

/** Something waiting — icon and number share one tinted surface. */
export const COUNT_PILL_ACTIVE =
  "gap-1 px-2 border border-[#ffd1d1] bg-[#ffebeb] text-[#ba2525] hover:bg-[#ffe0e0] dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-300 dark:hover:bg-rose-950/60"

/** The number itself. Tabular so the pill doesn't jitter as the count changes. */
export const COUNT_PILL_VALUE = "text-[11px] font-semibold leading-none tabular-nums"

/** One compact priority surface for account-health context + deal-health action. */
export const TIMELINE_NEXT_ACTION_SURFACE =
  "overflow-hidden rounded-xl border border-emerald-200/70 bg-[#f7fbf8] shadow-[0_1px_2px_rgba(28,40,64,0.05)] dark:border-emerald-900/70 dark:bg-emerald-950/20"

/** Leading glyph on the unified timeline next-action surface. */
export const TIMELINE_NEXT_ACTION_GLYPH =
  "grid size-7 shrink-0 place-items-center rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300"

/** Eyebrow label on the unified timeline next-action surface. */
export const TIMELINE_NEXT_ACTION_LABEL =
  "text-[10px] font-semibold uppercase tracking-[0.1em] text-[#047857] dark:text-emerald-400"

/** Compact direction glyph preceding an account-timeline email sender. */
export const TIMELINE_EMAIL_DIRECTION_ICON =
  "grid size-6 shrink-0 place-items-center rounded-full"

/** Incoming/outgoing tint for TIMELINE_EMAIL_DIRECTION_ICON. */
export const TIMELINE_EMAIL_DIRECTION_ICON_COLOR: Record<"incoming" | "outgoing", string> = {
  incoming:
    "bg-[#e0fced] text-[#007d53] dark:bg-emerald-950/50 dark:text-emerald-300",
  outgoing:
    "bg-[#e5eeff] text-[#215bc4] dark:bg-indigo-950/50 dark:text-indigo-300",
}

/** Borderless icon action inside compact timeline email cards. */
export const TIMELINE_EMAIL_ICON_BUTTON =
  "grid size-8 cursor-pointer place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring pointer-coarse:size-10"

/**
 * The top edge of one message in a stacked thread — a barely-there fade over
 * the first 20px, so a new email reads as its own block. Added 2026-09-04
 * (Jason: "the separation between emails to be a little bit more clear...
 * a very faint tiny gradient, very lightly, from the top"), once several
 * messages could be open at once and a hairline alone stopped being enough
 * to say where one email ended and the next began.
 *
 * Strength is 2.25% of the foreground colour: halved on 2026-09-05 after
 * Jason saw the first version on a real thread ("the gradient shade is too
 * much, can we reduce it 50%?"). It shipped at 4.5%, which read as a band
 * rather than an edge.
 *
 * Painted as the row's OWN background, never as an absolutely-positioned
 * overlay: a positioned child paints above the in-flow text beneath it, so an
 * overlay would tint the sender's name along with the gap. As a background it
 * sits behind every child.
 *
 * One expression serves both themes because the palette is neutral. Over the
 * white card `foreground` is near-black and reads as a soft inner shadow;
 * over the dark card it is near-white and reads as a lift. The far stop is
 * `foreground/0` rather than `transparent` so the interpolation fades out at
 * a constant hue instead of drifting toward transparent black.
 */
export const TIMELINE_MESSAGE_FADE =
  "bg-gradient-to-b from-foreground/[0.0225] to-foreground/0 bg-[length:100%_20px] bg-no-repeat"

/** Leading expand/collapse control inside compact timeline email cards. */
export const TIMELINE_EMAIL_TOGGLE =
  "grid size-8 shrink-0 cursor-pointer place-items-center rounded-lg border border-border bg-background text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring pointer-coarse:size-10"

/** Canonical dashboard-card heading. Pair with a `size-4 text-muted-foreground` icon. */
export const CARD_TITLE = "flex items-center gap-2 text-sm font-semibold tracking-tight"

/**
 * Stat/KPI-card title — the small label above a stat value (Health, Revenue,
 * Relationship, Delivery, Approvals on the account timeline strip). Inter 500
 * at 12px in 55%-black (#0000008c), with a white/55 dark fallback.
 */
export const STAT_TITLE = "text-xs font-medium text-black/55 dark:text-white/55"

/** One-line account KPI rail. Scrolls horizontally on compact screens. */
export const TIMELINE_KPI_STRIP =
  "flex min-w-max divide-x divide-border overflow-hidden rounded-lg border border-border bg-card lg:min-w-0"

/** One concise metric within TIMELINE_KPI_STRIP. */
export const TIMELINE_KPI_CELL =
  "flex h-10 w-max min-w-[150px] shrink-0 items-center gap-1.5 px-3 lg:w-auto lg:min-w-0 lg:flex-1"

/** Status is carried by the KPI segment background, not an extra dot. */
export const TIMELINE_KPI_TONE_BACKGROUND: Record<"green" | "amber" | "red", string> = {
  green: "bg-emerald-50/70 dark:bg-emerald-950/25",
  amber: "bg-amber-50/80 dark:bg-amber-950/25",
  red: "bg-rose-50/80 dark:bg-rose-950/25",
}

/** Content priority inside a compact KPI cell: value wins, label/detail yield. */
export const TIMELINE_KPI_LABEL =
  "min-w-0 truncate text-xs font-medium text-black/55 dark:text-white/55"
export const TIMELINE_KPI_VALUE =
  "shrink-0 whitespace-nowrap text-[13px] font-semibold tabular-nums text-foreground"
export const TIMELINE_KPI_DETAIL = "min-w-0 truncate text-[11px] text-muted-foreground"

/**
 * Document / long-form text — rendered markdown for SOPs, reports, strategy
 * docs, and anything authored as prose. Pairs with the Tailwind typography
 * plugin (`@plugin "@tailwindcss/typography"` in globals.css). Use this on the
 * container of `dangerouslySetInnerHTML`-rendered markdown (or a prose region)
 * so document text hierarchy — H1/H2, body, bold, lists, quotes, inline images
 * — reads the same everywhere. Green links, rounded images, tight headings.
 */
export const PROSE = cn(
  "prose prose-sm max-w-none dark:prose-invert",
  "prose-headings:font-semibold prose-headings:tracking-tight",
  "prose-a:text-emerald-700 prose-a:no-underline hover:prose-a:underline dark:prose-a:text-emerald-400",
  "prose-img:rounded-lg prose-strong:font-semibold",
)

/**
 * Large-dialog chrome ONLY — the styleguide's "Modal & dialog" frame: a 3px
 * frosted outer frame (border-border/50 over bg-popover/60, backdrop blur,
 * layered drop shadow, hairline ring) wrapping a solid rounded inner card.
 * Apply MODAL_FRAME to the floating element and put all content inside a
 * child carrying MODAL_FRAME_INNER.
 *
 * Do NOT use this on small anchored popovers (property pickers, cell editors,
 * dropdown menus). Those use the default PopoverContent chrome — rounded-lg
 * bg-popover shadow-md ring-1 ring-foreground/10 — with `p-0` plus a
 * search-header/list body when they're pickers. See the styleguide's
 * "Picker popover" section and AccountManagerCell in editable-cells.tsx for
 * the canonical example.
 */
export const MODAL_FRAME = cn(
  "rounded-2xl border border-border/50 bg-popover/60 p-[3px]",
  "shadow-[0px_8px_28px_-6px_#1c28401f,0px_18px_88px_-4px_#1c284029,0px_0px_1px_0px_#1c284029]",
  "ring-1 ring-foreground/10 backdrop-blur-[15px]",
)

export const MODAL_FRAME_INNER = "overflow-hidden rounded-[13px] bg-popover"

/**
 * Native `<select>` chrome. The browser-default arrow renders flush against
 * the right edge and can't be padded, so this strips it (appearance-none) and
 * draws our own chevron inset 12px from the edge, with pr-9 keeping the text
 * clear of it. zinc-500 (#71717a) reads on both light and dark fields.
 */
export const NATIVE_SELECT = cn(
  "w-full appearance-none rounded-md border border-input bg-background py-2 pl-3 pr-9 text-sm",
  "outline-none focus-visible:ring-2 focus-visible:ring-ring",
  "bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2371717a%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')]",
  "bg-[length:16px_16px] bg-[position:right_12px_center] bg-no-repeat",
)

/**
 * Segmented control (filter toggles, view switchers). A soft muted track holds
 * a row of segments; the selected segment lifts off as a white card with a soft
 * shadow and an emerald label — never a high-contrast black fill. `h-9` matches
 * SUBHEADER_CONTROL_H (the global sidebar-search height) so every subheader
 * toggle lines up with adjacent search fields, date pickers, and buttons.
 *
 * Usage:
 *   <div className={SEGMENT_TRACK}>
 *     {opts.map(o => (
 *       <button className={cn(SEGMENT_ITEM, active ? SEGMENT_ITEM_ON : SEGMENT_ITEM_OFF)}>…</button>
 *     ))}
 *   </div>
 */
export const SEGMENT_TRACK =
  "inline-flex h-9 items-center gap-0.5 rounded-md bg-muted/60 p-0.5 pointer-coarse:h-10"

/** Segmented-control item. `cursor-pointer` for the same Tailwind v4 reason as
 *  BUTTON_BASE — these render as <button> and would otherwise show an arrow. */
export const SEGMENT_ITEM =
  "inline-flex h-8 cursor-pointer items-center rounded-[5px] px-3 text-sm font-medium transition-colors pointer-coarse:h-9"

export const SEGMENT_ITEM_ON =
  "bg-white text-emerald-700 shadow-sm dark:bg-input/60 dark:text-emerald-300"

export const SEGMENT_ITEM_OFF =
  "text-muted-foreground hover:text-foreground"

/**
 * Underline tab row — the top-level RECORD switcher on a full-bleed page (the
 * /services catalog tabs). Deliberately distinct from SEGMENT_*: a segmented
 * control switches the VIEW of one record and floats in a muted track; a tab
 * row switches WHICH record you are looking at and sits on the page's top
 * border. Using a segment where a tab belongs makes "which service am I
 * editing" read as "which panel am I on".
 *
 * TAB_ROW scrolls horizontally on purpose. Any catalog outgrows the viewport
 * eventually, and a flex row with no overflow rule compresses its children into
 * unreadable slivers instead of scrolling — the trailing tabs become literally
 * unclickable. `shrink-0` on TAB_ITEM is what forces the scroll rather than the
 * squash; don't drop it.
 *
 * Usage:
 *   <div className={TAB_ROW}>
 *     {records.map(r => (
 *       <button className={cn(TAB_ITEM, active ? TAB_ITEM_ON : TAB_ITEM_OFF)}>…</button>
 *     ))}
 *   </div>
 */
export const TAB_ROW = "flex items-center overflow-x-auto border-b px-4"

export const TAB_ITEM =
  "shrink-0 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors"

export const TAB_ITEM_ON = "border-[#158E1D] text-foreground"

export const TAB_ITEM_OFF =
  "border-transparent text-muted-foreground hover:border-muted-foreground/30 hover:text-foreground"

// ── Workspace tabs (the top-bar strip) ──────────────────────────────────────

/**
 * NOT the same thing as TAB_ROW above. Those are IN-PAGE tabs — sections of one
 * record, drawn as an underlined row inside the page. These are WORKSPACE tabs:
 * the browser-style strip of open pages that lives in the OS top bar and answers
 * to ⌥1…⌥9. One is "which part of this record", the other is "which page".
 *
 * They must not look alike, so this family is a pill on a surface rather than an
 * underline: the active tab is a filled chip, the rest are quiet text. The strip
 * lives inside the 56px top bar and adds NO height of its own — a great many
 * pages size themselves with `h-[var(--pane-h)]`, and a second chrome row
 * would push every one of them off the bottom of the screen.
 *
 * Usage:
 *   <div className={WORKSPACE_TAB_STRIP}>
 *     {tabs.map(t => (
 *       <div className={cn(WORKSPACE_TAB, active ? WORKSPACE_TAB_ON : WORKSPACE_TAB_OFF)}>…</div>
 *     ))}
 *   </div>
 */
export const WORKSPACE_TAB_STRIP = cn(
  // Content-width, NOT flex-1. The strip's own container is the flexible one;
  // if the strip stretched, the controls after it (the options kebab) would be
  // shoved to the far right of the bar and read as belonging to the icons over
  // there rather than to the tabs. `min-w-0` still lets it shrink and scroll
  // once the tabs outgrow the space.
  "flex min-w-0 items-center gap-0.5 overflow-x-auto",
  // The strip scrolls rather than squashing its tabs into unreadable slivers
  // (same reason TAB_ITEM is shrink-0). A visible scrollbar inside a 32px-tall
  // row is all noise, so it's hidden — the tabs themselves are the affordance.
  "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
)

export const WORKSPACE_TAB = cn(
  "group/tab relative flex h-8 max-w-[190px] shrink-0 cursor-pointer items-center gap-1.5",
  "rounded-lg pl-2 pr-1.5 text-[13px] font-medium transition-colors select-none",
)

export const WORKSPACE_TAB_ON = "bg-muted text-foreground"

export const WORKSPACE_TAB_OFF =
  "text-muted-foreground hover:bg-muted/60 hover:text-foreground"

/** The × on a tab. Hidden until hover on the inactive ones; always on the active
 *  tab and on coarse pointers, where there is no hover to reveal it. */
export const WORKSPACE_TAB_CLOSE = cn(
  "flex size-4 shrink-0 items-center justify-center rounded",
  "text-muted-foreground/70 hover:bg-foreground/10 hover:text-foreground",
  "opacity-0 group-hover/tab:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100",
)

/**
 * Inline numeric field inside a dense data row — the hours / quantity / price
 * cells in the service designer, where a column of numbers is edited in place.
 * Borderless until hovered or focused so a table of figures reads as text
 * rather than a wall of input boxes, and `tabular-nums` keeps the column
 * aligned while typing.
 *
 * NOT for form fields — a form input should look like an input before you touch
 * it. Use the `Input` component there.
 */
export const INLINE_NUM_INPUT = cn(
  "h-8 rounded-md border border-transparent bg-transparent px-1.5 text-[13px] tabular-nums",
  "hover:border-input focus:border-input",
  "outline-none focus-visible:ring-2 focus-visible:ring-ring",
)

/**
 * Hover-revealed row action (kebabs, delete buttons, quick actions inside
 * table rows and cards). Hidden until the row is hovered on fine-pointer
 * devices; ALWAYS visible on coarse pointers (touch has no hover) and when
 * keyboard-focused. Never hand-roll `opacity-0 group-hover:opacity-100` on an
 * interactive control without the coarse-pointer + focus escape hatches —
 * that's how actions become unreachable on phones. Requires `group` on the
 * hovered ancestor. See the "Mobile & touch" styleguide section.
 */
export const HOVER_REVEAL =
  "opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"

/**
 * Drill-down selection — the "this filter is ON" chrome for stackable grid
 * selectors (a clicked KPI tile, a focused month/week column, a narrowed
 * Total-component row). Deliberately loud — purple wash + 2px inset ring — so
 * when several layers are active at once (tile + month + component) each one
 * reads at a glance and the user can see how deep the drill goes. Pair
 * DRILL_ON on the container with DRILL_TEXT on the label/value it highlights.
 */
export const DRILL_ON =
  "bg-violet-600/10 ring-2 ring-inset ring-violet-600 dark:bg-violet-400/10 dark:ring-violet-400"

export const DRILL_TEXT = "text-violet-700 dark:text-violet-300"

/** Company glyph (rounded square) / person glyph (rounded-full) base. */
export function glyphClass(name: string, shape: "company" | "person" = "company"): string {
  return cn(
    "inline-flex size-5 shrink-0 items-center justify-center text-[10px] font-semibold",
    shape === "person" ? "rounded-full" : "rounded-[5px]",
    colorForName(name),
  )
}

/* ── Financial statement bands ───────────────────────────────────────────
 * A statement-shaped sheet (the /finance P&L) reads like Tom's workbook:
 * a navy section bar, a light-blue group band, grey subtotals, the three
 * headline lines (net revenue, gross profit, EBITDA) on green, memo rows on
 * a faint ground. Keyed by row kind so every statement bands the same way;
 * the sheet's header row shares the section navy.
 */
export const LEDGER_BAND: Record<"section" | "group" | "subtotal" | "metric" | "memo" | "highlight" | "estimate", string> = {
  section: "bg-[#1f3b4d] dark:bg-[#132633]",
  group: "bg-[#dbe6ee] dark:bg-[#1c3242]",
  subtotal: "bg-[#e6e8ea] dark:bg-[#26303a]",
  metric: "bg-[#f3f4f6] dark:bg-[#1f262e]",
  memo: "bg-[#fafafa] dark:bg-[#161b21]",
  highlight: "bg-[#c6efce] dark:bg-[#0f3d2a]",
  /** A projected month ("Oct est"): the workbook's grey-blue block, figures in italics. */
  estimate: "bg-[#eef2f5] dark:bg-[#151d24]",
}
/** The header of a projected month — the group band's blue, italic, navy type. */
export const LEDGER_ESTIMATE_HEADER = "bg-[#dbe6ee] italic text-[#1f3b4d] dark:bg-[#1c3242] dark:text-[#cfe3f0] [&_span]:text-[#1f3b4d] dark:[&_span]:text-[#cfe3f0]"
/** Navy ink for labels and figures on the light bands. */
export const LEDGER_INK = "text-[#1f3b4d] dark:text-[#cfe3f0]"
/** The header row of a statement sheet — the section navy, white type. */
export const LEDGER_HEADER = "bg-[#1f3b4d] text-white dark:bg-[#132633] [&_span]:text-white"

/* ── Sheet chrome ────────────────────────────────────────────────────────
 * The name box + formula bar above a DataSheet (finance sheets, and any
 * sheet on the engine): the active cell's address, an "fx" glyph, the
 * cell's contents to read or type into, and the column + row it belongs
 * to. Hairline dividers to match the sheet's header row.
 */
export const SHEET_FORMULA_BAR = "flex h-8 shrink-0 items-stretch border-b border-border/60 bg-background text-[13px]"
export const SHEET_NAME_BOX = "flex w-[76px] shrink-0 items-center justify-center border-r border-border/60 font-mono text-[12px] text-muted-foreground"
export const SHEET_FX_GLYPH = "flex w-9 shrink-0 select-none items-center justify-center border-r border-border/60 font-serif italic text-muted-foreground"
export const SHEET_FORMULA_INPUT = "min-w-0 flex-1 bg-transparent px-3 outline-none"
export const SHEET_FORMULA_HINT = "hidden max-w-[40%] shrink-0 items-center truncate px-3 text-[11px] text-muted-foreground md:flex"

/* ── Tasks (Phase 1, 2026-10-02) ──────────────────────────────────────────
 * A task's status chip is coloured by its GROUP (open · active · review ·
 * parked · done · closed), never by the status name — people rename names,
 * the group is what reports read. Same pastel family as STATUS_COLOR.
 */
export const TASK_GROUP_COLOR: Record<string, string> = {
  open: "border border-[#e6e7ea] bg-[#f3f4f6] text-[#5f5e5b] dark:border-zinc-700/50 dark:bg-zinc-800/60 dark:text-zinc-300",
  active: "border border-[#d6e5ff] bg-[#e5eeff] text-[#215bc4] dark:border-sky-900/40 dark:bg-sky-950/40 dark:text-sky-300",
  review: "border border-[#e9d9ff] bg-[#f3ebff] text-[#6d3fc4] dark:border-violet-900/40 dark:bg-violet-950/40 dark:text-violet-300",
  parked: "border border-[#fbe6bf] bg-[#fef4e0] text-[#b06f12] dark:border-amber-900/40 dark:bg-amber-950/40 dark:text-amber-300",
  done: "border border-[#cbf7e1] bg-[#e0fced] text-[#007d53] dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-300",
  closed: "border border-[#e6e7ea] bg-[#f3f4f6] text-[#9a9a97] line-through dark:border-zinc-700/50 dark:bg-zinc-800/60 dark:text-zinc-500",
}
export const TASK_GROUP_DOT: Record<string, string> = {
  open: "bg-zinc-400",
  active: "bg-sky-500",
  review: "bg-violet-500",
  parked: "bg-amber-500",
  done: "bg-emerald-500",
  closed: "bg-zinc-300",
}
/** A client input is a dated dependency, never a staff-hours bar. */
export const TASK_CLIENT_REQUEST_ROW = "sticky left-0 z-[5] flex w-[300px] shrink-0 items-center gap-2 border-r border-border/60 bg-background px-4 text-left text-[13px] transition-colors hover:bg-muted/40 focus-visible:outline-2 focus-visible:outline-emerald-600"
export const TASK_CLIENT_REQUEST_MARKER = "absolute top-2 inline-flex h-7 max-w-[240px] items-center gap-1.5 rounded-md border px-2 text-left text-[12px] font-medium transition hover:brightness-95 focus-visible:outline-2 focus-visible:outline-emerald-600"
export const TASK_CLIENT_REQUEST_COLOR = { slate: STATUS_COLOR.inactive, amber: HEALTH_COLOR.amber, red: HEALTH_COLOR.red } as const
/** The human task key ("WEB-143") — mono, muted, tabular, never wraps. */
export const TASK_KEY = "shrink-0 font-mono text-[11px] font-medium tabular-nums text-muted-foreground"
/* ── Tasks Phase 2 (2026-10-03) — the ClickUp-shaped task page and views ──
 * A field row is "icon + muted label | value that reads as text and lights up
 * on hover" (ClickUp's layout, Jason). Every value opens a typeable picker;
 * none of them looks like a dropdown.
 */
/** The label column of a task field row. */
export const TASK_FIELD_LABEL = "flex h-8 w-[150px] shrink-0 items-center gap-2 text-[13px] text-muted-foreground"
/** The value of a task field row: plain text until hovered, then a soft pill. */
export const TASK_FIELD_VALUE = "inline-flex h-8 max-w-full items-center gap-1.5 rounded-md px-2 text-left text-[13px] text-foreground hover:bg-muted"
/** ClickUp's status pill on the task page: uppercase, coloured by group. */
export const TASK_STATUS_PILL = "inline-flex h-[26px] items-center gap-1 rounded-md pl-2.5 pr-1.5 text-[11px] font-semibold uppercase tracking-wide"
/** Priority flag colour by level (urgent · high · normal · low); `none` draws an outline. */
export const TASK_PRIORITY_COLOR: Record<string, string> = {
  urgent: "text-rose-500",
  high: "text-amber-500",
  normal: "text-sky-500",
  low: "text-zinc-400",
  none: "text-muted-foreground/60",
}
/* ── Task panel, three levels (2026-10-03, Jason's F/G mockups) ──
 * At rest the panel shows only what is set. One click anywhere opens the
 * affordances (empty-state boxes, add chips). A click on the field area opens
 * every field; a click on the description opens just that editor,
 * in place. Escape steps back one level. Label above value, two columns.
 */
/** The small label above a field value in the two-column grid. */
/** Compact task hierarchy: readable context followed by the parent title. */
export const TASK_BREADCRUMB = "flex min-w-0 flex-1 items-center gap-1 overflow-hidden text-[12px] text-muted-foreground"
export const TASK_PANEL_HEADER = "flex shrink-0 flex-wrap items-center gap-1.5 gap-y-0 border-b border-border/60 px-4 py-1 text-[12px] text-muted-foreground max-phone:flex-nowrap max-phone:px-3"
export const TASK_BREADCRUMB_CONTEXT = "max-w-[35%] shrink-0 max-phone:max-w-[50%] max-phone:shrink"
export const TASK_BREADCRUMB_ITEM = "inline-flex min-w-0 items-center gap-1 rounded-md py-0.5 hover:bg-muted hover:text-foreground"
/** Recorded time is on target only at 90–110% of weekly capacity. */
export const TIMESHEET_LOAD_FILL = { under: HEALTH_DOT.amber, target: HEALTH_DOT.green, over: HEALTH_DOT.red, unknown: STATUS_DOT.inactive }
export const TIMESHEET_LOAD_TRACK = "inline-block h-1 w-12 shrink-0 overflow-hidden rounded-full bg-border"
export const TASK_CELL_LABEL = "text-[11px] tracking-[0.02em] text-muted-foreground"
/** A field value in the grid: plain text, a soft pill on hover; the panel adds an inset ring while every field is open. */
export const TASK_CELL_VALUE = "-ml-2 inline-flex h-[30px] max-w-full items-center gap-2 overflow-hidden whitespace-nowrap rounded-md px-2 text-left text-[13px] text-foreground transition-colors hover:bg-muted"
/** The detail grid stays two columns on phones; long field values wrap within their cell. */
export const TASK_PANEL_FIELDS = "-mx-3 grid cursor-default grid-cols-2 gap-x-6 rounded-xl px-3 py-1 transition-colors hover:bg-muted/30 max-phone:gap-x-4"
/** Phone task rows keep their summary in the viewport; the desktop table stays dense. */
export const TASK_PHONE_ROW = "flex h-24 items-center gap-2 border-b border-border/50 px-3 text-[13px] transition-colors hover:bg-muted/40"
export const TASK_PHONE_TITLE = "line-clamp-2 text-left text-[14px] font-medium leading-5"
/** A touch target without changing desktop control density. */
// Compensate for the app shell's phone zoom; portalled drawers fall back to 1.
export const TASK_TOUCH_CONTROL = "max-phone:min-h-[calc(2.75rem/var(--app-zoom,1))] max-phone:min-w-[calc(2.75rem/var(--app-zoom,1))]"
export const TASK_PANEL_VALUE = cn(TASK_CELL_VALUE, TASK_TOUCH_CONTROL, "max-phone:h-auto max-phone:whitespace-normal max-phone:py-1 max-phone:leading-5 max-phone:[overflow-wrap:anywhere] max-phone:[&_.truncate]:whitespace-normal max-phone:[&_.truncate]:overflow-visible")
/** The task drawer uses the same frosted frame as large dialogs, with safe-area padding. */
export const TASK_MOBILE_PANEL = cn(MODAL_FRAME, "gap-0 overflow-hidden outline-none data-[side=bottom]:inset-x-2 data-[side=bottom]:bottom-0 data-[side=bottom]:h-[var(--task-panel-height,calc(100dvh-env(safe-area-inset-top)-1rem))] rounded-b-none translate-y-[var(--task-drag-y,0px)] transition-[height,translate,opacity] data-[dragging=true]:transition-none motion-reduce:transition-none")
export const TASK_MOBILE_PANEL_INNER = cn(MODAL_FRAME_INNER, "flex h-full min-h-0 flex-col rounded-b-none pb-[env(safe-area-inset-bottom)]")
/** Full-width touch surface; a tucked task keeps its title beneath the small line. */
export const TASK_PANEL_HANDLE = "flex h-11 w-full shrink-0 touch-none select-none items-center justify-center rounded-t-[13px] bg-popover data-[collapsed=true]:flex-col data-[collapsed=true]:gap-1 data-[collapsed=true]:px-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-600"
export const TASK_PANEL_HANDLE_LINE = "h-1 w-10 shrink-0 rounded-full bg-muted-foreground/40"
export const TASK_PANEL_COLLAPSED_TITLE = "block w-full truncate text-center text-[12px] font-medium leading-4 text-foreground/80"
/** Leave the 50px tucked drawer and a 16px gap below bottom notifications.
 *  Keyed on `body[data-task-drawer-collapsed]`, which ResponsiveTaskPanel sets
 *  from an effect (with cleanup) while a drawer is tucked — never `:has()` on
 *  a sheet, because `<Activity>` keeps hidden tabs' sheets mounted (Rule 19). */
export const TASK_PANEL_TOAST_CLEARANCE = "[body[data-task-drawer-collapsed]_&[data-y-position=bottom]]:bottom-[calc(66px+env(safe-area-inset-bottom))]!"
/** The big dashed box that stands in for an empty description — the whole box is the click target. */
export const TASK_PROMPT_BOX = "block w-full rounded-[10px] border border-dashed border-border px-3.5 py-3 text-left text-[14px] leading-relaxed text-muted-foreground transition-colors hover:border-foreground/30 hover:bg-muted/40 hover:text-foreground/80"
/** A dashed "＋ Subtask" style chip that appears once the panel is open. */
export const TASK_ADD_CHIP = "inline-flex h-[30px] items-center gap-1.5 rounded-full border border-dashed border-border px-3 text-[13px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
/** A one-row toolbar control: 32px, never wraps, chevron after the label (Tasks toolbar). */
export const TASK_TOOLBAR_BUTTON = "inline-flex h-8 shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-border bg-background px-2.5 text-[12px] font-medium text-foreground hover:bg-muted"
/** The view switcher: a muted tab list with a white, shadowed active pill. */
export const TASK_VIEW_TABS = "inline-flex h-9 items-center gap-0.5 rounded-lg bg-muted p-[3px]"
export const TASK_VIEW_TAB = "inline-flex h-[30px] items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-muted-foreground transition-colors hover:text-foreground"
export const TASK_VIEW_TAB_ON = "bg-background text-foreground shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)]"
/** A pastel bar on the workload timeline / gantt, coloured by status group. */
export const TASK_BAR_COLOR: Record<string, string> = {
  open: "bg-[#f3f4f6] text-[#3f3f46] dark:bg-zinc-800 dark:text-zinc-200",
  active: "bg-[#e5eeff] text-[#215bc4] dark:bg-sky-950/60 dark:text-sky-200",
  review: "bg-[#f3ebff] text-[#6d3fc4] dark:bg-violet-950/60 dark:text-violet-200",
  parked: "bg-[#fef4e0] text-[#b06f12] dark:bg-amber-950/60 dark:text-amber-200",
  done: "bg-[#e0fced] text-[#007d53] dark:bg-emerald-950/60 dark:text-emerald-200",
  closed: "bg-[#f3f4f6] text-[#9a9a97] dark:bg-zinc-800 dark:text-zinc-500",
}
/** The service group a list belongs to (the rail, the Lists cards, list settings): one dot colour each. */
export const TASK_SERVICE_COLOR: Record<string, string> = {
  Social: "bg-violet-500",
  Web: "bg-sky-500",
  Email: "bg-amber-500",
  SEO: "bg-emerald-500",
  PPC: "bg-rose-500",
  "Design & Photo": "bg-fuchsia-600",
  Strategy: "bg-cyan-500",
  Other: "bg-zinc-400",
}
export const taskServiceColor = (group: string | null | undefined) => TASK_SERVICE_COLOR[group ?? "Other"] ?? TASK_SERVICE_COLOR.Other
/** The selected row or lane: a soft leaf tint; a row in the bulk selection is fainter. */
export const TASK_ROW_SELECTED = "bg-emerald-50/60 dark:bg-emerald-950/20"
export const TASK_ROW_IN_BULK = "bg-emerald-50/40 dark:bg-emerald-950/10"
/** Client board: an empty status as a slim dashed strip with its name running down it (Tom 2026-11-02, design 4A). */
export const TASK_BOARD_SLIM = "flex min-h-40 w-9 shrink-0 flex-col items-center gap-2 rounded-xl border border-dashed border-border/70 py-2.5 text-muted-foreground"
/** Client board, while a card is dragged: a status the card's own list does not have. Hatched, refuses the drop. */
export const TASK_BOARD_BLOCKED = "cursor-not-allowed bg-[repeating-linear-gradient(135deg,rgb(228_228_231/0.6)_0_6px,transparent_6px_12px)] opacity-70 dark:bg-[repeating-linear-gradient(135deg,rgb(63_63_70/0.6)_0_6px,transparent_6px_12px)]"
/** The 2px leaf stripe on the left edge of the selected list row (and a column being dragged over). */
export const TASK_SELECTION_STRIPE = "shadow-[inset_2px_0_0_#158E1D]"
/** Today on a day grid: the date in a leaf disc (calendar, timeline header) and the vertical line through the lanes. */
export const TASK_TODAY_PILL = "inline-flex size-5 items-center justify-center rounded-full bg-emerald-600 font-semibold text-white"
export const TASK_TODAY_LINE = "bg-emerald-500/70"
/** Gantt chrome: the grey span bar per group, dependency arrows (stroke=currentColor), a late bar's edge. */
export const TASK_GANTT_SPAN = "bg-zinc-300 dark:bg-zinc-700"
export const TASK_GANTT_ARROW = "text-zinc-400 dark:text-zinc-600"
export const TASK_GANTT_LATE = "border-rose-400"
/** Gantt: a parent is a tinted band behind its subtasks' rows (Tom 2026-11-02, design 3C); its header text. */
export const TASK_GANTT_BAND = "border-x border-[#d6e5ff] bg-[#f5f8ff] dark:border-sky-900/50 dark:bg-sky-950/30"
export const TASK_GANTT_BAND_TEXT = "text-[#215bc4] dark:text-sky-300"
/** The bracket down the names column joining a parent's subtasks. */
export const TASK_GANTT_BAND_LINE = "bg-[#bcd3ff] dark:bg-sky-800/60"
/** Weekend shade and the Monday hairline are painted as ONE repeating gradient
 *  per lane (F-T30), so they are CSS colours, not classes. */
export const TASK_GANTT_WEEKEND = "color-mix(in oklab, var(--muted) 60%, transparent)"
export const TASK_GANTT_WEEK_LINE = "color-mix(in oklab, var(--border) 60%, transparent)"
/** Hours-per-day shade behind workload bars: rgb channels; the alpha scales with the load. */
export const TASK_GANTT_LOAD_RGB = { over: "239 68 68", near: "245 158 11", under: "34 197 94" } as const
/** A client request's tone as a vertical line (border-l) or an SVG path (stroke=currentColor). */
export const TASK_CLIENT_REQUEST_LINE = { slate: "border-zinc-400 text-zinc-400", amber: "border-amber-400 text-amber-400", red: "border-rose-400 text-rose-400" } as const
/** Due-date text tone on rows and cards. */
export const TASK_DUE_TONE: Record<string, string> = { late: "text-rose-600 dark:text-rose-400", today: "text-amber-700 dark:text-amber-400", soon: "text-foreground", none: "text-muted-foreground" }
/** "ClickUp is behind" on a row or in the panel. */
export const TASK_SYNC_BEHIND = "text-amber-700 dark:text-amber-400"
/** Text-only destructive action (Delete) and its confirm step. */
export const TASK_DANGER_TEXT = "text-rose-700 dark:text-rose-400"
export const TASK_DANGER_OUTLINE = "border-rose-300 dark:border-rose-900/60"
/** A quiet inline link ("Plan across teams →", "Board"). */
export const TASK_LINK = "text-emerald-700 hover:underline dark:text-emerald-400"
/** Task History tab (Tom 2026-11-02, design 2A): a description line taken out, and one put in. */
export const TASK_DIFF_LINE = {
  removed: "bg-rose-50 text-rose-900 line-through decoration-rose-300 dark:bg-rose-950/30 dark:text-rose-200",
  added: "bg-emerald-50 text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200",
} as const
/** Assignee picker (Tom 2026-11-02, design 1B): free hours beside a name — a short meter that fills when they have what the task needs, and the hours. */
export const TASK_FREE_HOURS = {
  track: "h-1 w-10 overflow-hidden rounded-full bg-muted",
  ok: { bar: "bg-emerald-500", text: "font-medium text-emerald-700 dark:text-emerald-400" },
  short: { bar: "bg-rose-500", text: "font-medium text-rose-700 dark:text-rose-400" },
} as const
/** Latest comment stays within the 44px task row; metadata shares one line. */
export const TASK_COMMENT_PREVIEW = 'block min-w-0 max-w-full text-[12px] leading-4'
export const TASK_COMMENT_META = 'block truncate text-[10px] text-muted-foreground'
/** The flat primary for small in-panel submits (Post · Log · Save view · Create): emerald-600 without PRIMARY_BUTTON's shadow. */
export const PRIMARY_BUTTON_FLAT = "bg-emerald-600 text-white hover:bg-emerald-700"
/** A text field's focus: a thin leaf border, no halo. */
export const TASK_INPUT_FOCUS = "focus:border-emerald-500"
export const TASK_COMMENT_INPUT = cn("block max-h-40 min-h-14 w-full resize-y rounded-[10px] border border-border bg-background px-3 py-2.5 text-[13px] leading-relaxed outline-none disabled:opacity-60", TASK_INPUT_FOCUS)
/** A toggle that is on (Just me · the chosen service group chip). */
export const TASK_TOGGLE_ON = "border-emerald-500 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300"
/** The desktop task panel's drag handle: a hairline that turns leaf on hover. */
export const TASK_PANEL_RESIZE_HANDLE = "hidden w-1.5 shrink-0 cursor-col-resize border-l border-border/60 hover:bg-emerald-500 lg:block"
/** The live dot inside a timer / "live" indicator. */
export const TASK_LIVE_DOT = "bg-emerald-500"
/** A row or cell highlighted for "today" in the timesheet. */
export const TASK_TODAY_TEXT = "text-emerald-700 dark:text-emerald-400"
/** Capacity is the same Tasks data on a people × time canvas. Sticky headers
 * and person names, compact task spans and milestone diamonds are shared. */
export const CAPACITY_GRID = 'relative min-h-0 flex-1 overflow-auto rounded-lg border border-border/60 bg-background'
export const CAPACITY_HEADER = 'sticky top-0 z-20 border-b border-border/60 bg-background'
export const CAPACITY_PERSON = 'sticky left-0 z-10 border-r border-border/60 bg-background px-3 py-2'
export const CAPACITY_CELL = 'relative border-r border-border/40 bg-background last:border-r-0'
export const CAPACITY_DAY_OFF = 'bg-muted/40'
/** Today marks the date without replacing the day's allocation colour. */
export const CAPACITY_DAY_TODAY = 'ring-1 ring-inset ring-emerald-300 dark:ring-emerald-700'
/** Daily allocation has its own meaning, separate from task deadline colours. */
export const CAPACITY_LOAD_BADGE_COLOR = { green: HEALTH_COLOR.green, amber: HEALTH_COLOR.amber, red: HEALTH_COLOR.red, neutral: STATUS_COLOR.inactive } as const
export const CAPACITY_LOAD_CELL_COLOR = {
  green: `${HEALTH_COLOR.green} border-0 border-r border-border/40 text-foreground`,
  amber: `${HEALTH_COLOR.amber} border-0 border-r border-border/40 text-foreground`,
  red: `${HEALTH_COLOR.red} border-0 border-r border-border/40 text-foreground`,
  neutral: 'bg-background',
} as const

// ── All staff day card (Jason 2026-10-09) ──────────────────────────────────
// One card per person for one day. Quiet at rest: the meter is the only colour
// on the card (no outline, no stripe, no dot); the detail layer shows on hover
// or focus, with its space reserved so nothing moves. Demonstrated on the
// styleguide under "All staff day card".
export const STAFF_CARD = 'group relative flex min-w-0 flex-col overflow-hidden rounded-[10px] border border-border bg-card transition-[box-shadow,border-color] duration-150 data-[drop=true]:border-emerald-500 data-[drop=true]:shadow-[0_0_0_1px_var(--color-emerald-500),0_8px_24px_rgba(28,40,64,.14)]'
export const STAFF_CARD_OFF = 'bg-sidebar'
export const STAFF_METER = 'relative mx-3.5 mb-2 h-1.5 overflow-hidden rounded-[3px] bg-muted'
/** The meter fill, keyed by CapacityLoadTone. Over-capacity hours draw as stripes on top. */
export const STAFF_METER_FILL: Record<'green' | 'amber' | 'red' | 'neutral', string> = {
  green: 'absolute inset-y-0 left-0 rounded-[3px] bg-emerald-500',
  amber: 'absolute inset-y-0 left-0 rounded-[3px] bg-amber-500',
  red: 'absolute inset-y-0 left-0 rounded-[3px] bg-rose-500',
  neutral: 'absolute inset-y-0 left-0 rounded-[3px] bg-zinc-300 dark:bg-zinc-600',
}
export const STAFF_METER_SPILL = 'absolute inset-y-0 left-0 rounded-[3px] opacity-55 [background:repeating-linear-gradient(45deg,currentColor_0,currentColor_3px,transparent_3px,transparent_6px)]'
/** The booked / capacity figure, in the meter's ink. */
export const STAFF_LOAD_INK: Record<'green' | 'amber' | 'red' | 'neutral', string> = {
  green: 'text-[#007d53] dark:text-emerald-300', amber: 'text-[#b25f00] dark:text-amber-300', red: 'text-[#ba2525] dark:text-rose-300', neutral: 'text-muted-foreground',
}
/** Hidden at rest, shown when the card is hovered, focused or a drop target. */
export const STAFF_DETAIL = 'opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 group-data-[drop=true]:opacity-100 motion-reduce:transition-none'
export const STAFF_ROW = 'grid w-full cursor-grab grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-md border border-transparent px-1.5 py-1.5 text-left hover:bg-muted/40 data-[review=true]:border-border data-[review=true]:bg-muted/40 data-[dragging=true]:opacity-40'
export const STAFF_EMPTY = 'px-1.5 py-2 text-[13px] font-medium text-[#ba2525] dark:text-rose-300'
export const CAPACITY_TODAY_HEADER = 'bg-emerald-600 text-white'
export const CAPACITY_DROP = 'ring-2 ring-inset ring-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/30'
export const CAPACITY_TASK = 'group flex h-7 max-phone:h-[calc(3rem/var(--app-zoom,1))] min-w-0 items-center gap-1 overflow-hidden rounded-md border border-foreground/10 px-1.5 text-[11px] shadow-sm select-none transition-[filter] duration-150 hover:brightness-95 dark:hover:brightness-125 motion-reduce:transition-none'
export const CAPACITY_TASK_CONTENT = 'flex min-w-0 flex-1 self-stretch items-center gap-1 text-left'
export const CAPACITY_TASK_TITLE = 'min-w-0 flex-1 truncate font-medium leading-4'
/** Keep hours at the right edge; narrow bars retain a title and expose full values on hover. */
export const CAPACITY_TASK_META = 'ml-auto block max-w-[60%] shrink-0 truncate text-right text-[9px] leading-4 tabular-nums'
/** Logged / estimated task hours: overruns retain the original estimate. */
export const CAPACITY_TASK_OVERRUN = `${CHIP} ${HEALTH_COLOR.red} px-1 py-0 text-[9px] leading-3 whitespace-nowrap`
/** Full task fills encode timing, independently of workflow status. */
export const CAPACITY_TASK_COLOR = { late: HEALTH_COLOR.red, on_time: STATUS_COLOR.prospect, ahead: HEALTH_COLOR.green, forecast_late: HEALTH_COLOR.amber, unknown: STATUS_COLOR.inactive } as const
/** Only review/approval waiting gets a thick task border. */
export const CAPACITY_TASK_APPROVAL = 'border-[3px] border-foreground/60'
/** Selection keeps the timing fill and approval border, using only a left stripe. */
export const CAPACITY_TASK_SELECTED = 'shadow-[inset_3px_0_0_var(--color-emerald-600),0_1px_2px_0_rgb(0_0_0/0.05)]'
/** Re-plan preview (Jason 2026-10-09): a proposed slot, never a real bar. Dashed and
 * transparent so it reads as "would be", not draggable, no resize handles. */
export const CAPACITY_GHOST = 'group flex h-7 max-phone:h-[calc(3rem/var(--app-zoom,1))] min-w-0 items-center gap-1 overflow-hidden rounded-md border-2 border-dashed bg-background/60 px-1.5 text-[11px] select-none'
/** A plain move is emerald; a ghost landing past a confirmed client date is rose, the flag Jason asked for. */
export const CAPACITY_GHOST_COLOR = { move: 'border-emerald-500/70 text-emerald-900 dark:text-emerald-200', past_client_date: 'border-rose-500/80 text-rose-900 dark:text-rose-200' } as const
/** The per-ghost approve tick: hidden at rest, shown on hover or focus (the STAFF_DETAIL idea). */
export const CAPACITY_GHOST_TICK = 'ml-auto flex size-5 shrink-0 items-center justify-center rounded text-emerald-700 opacity-0 transition-opacity hover:bg-emerald-100 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 dark:text-emerald-300 dark:hover:bg-emerald-900/40 motion-reduce:transition-none'
/** The strip above the Capacity grid that holds the re-plan toggle, its numbers and Approve all. */
export const REPLAN_STRIP = 'flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-emerald-200 bg-emerald-50/50 px-3 py-2 text-[12px] dark:border-emerald-900/60 dark:bg-emerald-950/20'
/** Client name fills the row; count and delivery status stay together at the end. */
export const TASK_CLIENT_ROW = 'grid min-h-9 grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-x-2 rounded-md px-2 py-1.5 text-[13px] text-foreground hover:bg-muted/60'
export const TASK_CLIENT_META = 'inline-flex items-center gap-1.5 whitespace-nowrap'
export const TASK_CLIENT_DELIVERY = 'whitespace-nowrap px-1.5 text-[11px]'

// Focused Tasks sidebar: one search, category tabs and a single scrolling body.
export const TASK_FOCUSED_RAIL = 'flex min-h-0 shrink-0 flex-col gap-3 overflow-hidden bg-sidebar px-3 py-3'
export const TASK_FOCUSED_RAIL_WIDTH = 'w-[320px] border-r border-border/60'
export const TASK_RAIL_ROW = 'relative flex min-h-9 items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] text-foreground transition-colors hover:bg-muted/60 pointer-coarse:min-h-11'
export const TASK_RAIL_SELECTED = 'bg-emerald-500/8 font-medium before:absolute before:inset-y-2 before:left-0 before:w-0.5 before:rounded-full before:bg-emerald-600'
export const TASK_RAIL_COMPACT = 'min-h-8 py-1'
export const TASK_RAIL_CATEGORY = 'flex items-center justify-between px-2 pb-2 pt-1 text-[12px] text-muted-foreground'
export const TASK_RAIL_TABS = 'w-full shrink-0 border-b border-border/60 pb-2'
export const TASK_RAIL_TAB = 'text-[12px] data-active:text-emerald-700 dark:data-active:text-emerald-300 after:bg-emerald-600 pointer-coarse:min-h-11'
export const CAPACITY_DIAMOND = 'size-3.5 shrink-0 rotate-45 rounded-[2px] border border-current'
export const CAPACITY_LINK_COLOR = { on_time: '#10b981', late: '#f43f5e', unscheduled: '#f59e0b' } as const
/** Adjustable Tasks rail: sized sections with independent scroll areas. */
export const TASK_RAIL_SECTION = 'flex min-h-28 shrink-0 flex-col overflow-hidden max-phone:min-h-36'
export const TASK_RAIL_HEADER = 'flex h-8 shrink-0 items-center gap-1 rounded-md pr-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground max-phone:h-[calc(2.75rem/var(--app-zoom,1))]'
export const TASK_RAIL_GRIP = 'flex size-6 shrink-0 cursor-grab touch-none items-center justify-center rounded text-muted-foreground/60 hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing max-phone:size-[calc(2.75rem/var(--app-zoom,1))]'
export const TASK_RAIL_RESIZE = 'group flex h-2 shrink-0 cursor-row-resize touch-none items-center px-1 hover:bg-emerald-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring max-phone:h-[calc(1rem/var(--app-zoom,1))]'
export const TASK_RAIL_DROP = 'ring-1 ring-inset ring-emerald-500 bg-emerald-500/10'
export const TASK_RAIL_SEARCH = 'max-phone:h-[calc(2.75rem/var(--app-zoom,1))] [&_input::-webkit-search-cancel-button]:appearance-none'
/** The chip on a row that says "2/3" subtasks are done. */
export const TASK_SUBTASK_CHIP = "inline-flex h-4 items-center rounded-full border border-border px-1.5 text-[10px] tabular-nums text-muted-foreground"
/** The running-timer pill: emerald live dot + elapsed figures. */
export const TIMER_PILL =
  "inline-flex items-center gap-1.5 rounded-[5px] border border-[#cbf7e1] bg-[#e0fced] px-1.5 py-0.5 text-[12px] font-semibold tabular-nums text-[#007d53] dark:border-emerald-900/40 dark:bg-emerald-950/40 dark:text-emerald-300"


/** Collapsed client commitment summary, expanded into the existing promise grid. */
export const PROMISE_CLIENT_SUMMARY = "flex w-full items-center gap-2 py-2 text-left text-[13px] normal-case tracking-normal"
